// ゲーム機の画面（frame）内部に閉じ込めて表示する Modal の土台。
// document.body へポータルする MUI Dialog をやめ、PieceBuilderTopPage の
// root（= 筐体画面 1124x572, transform: scale 配下）にオーバーレイを描画する。
// frame 側で既に scale が効いているため、各 Modal は手動 scale を当てない。
import { createPortal } from "react-dom";
import { ReactNode, createContext, useContext, useEffect, useRef, useState } from "react";

const ScreenModalContext = createContext<HTMLElement | null>(null);

export const useScreenModalContainer = () => useContext(ScreenModalContext);

// frame 内に Modal 描画先（pointer-events-none のオーバーレイ）を用意し Context で配布する
export const ScreenModalProvider = ({ children }: { children: ReactNode }) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  return (
    <ScreenModalContext.Provider value={container}>
      {children}
      <div
        ref={setContainer}
        className="flex-col-el absolute inset-0 z-[10001] pointer-events-none"
      />
    </ScreenModalContext.Provider>
  );
};

// frame 内に表示する共通 Modal シェル。dim backdrop ＋ 中央寄せのみ提供し、
// 中身（カード）は children 側が持つ。scale は frame から継承するため再適用しない。
export const InScreenModal = ({
  open,
  children,
  onBackdropClose,
}: {
  open: boolean;
  children: ReactNode;
  // 指定時のみ、背景（dim）タップで閉じる。入力途中は undefined を渡して閉じない。
  onBackdropClose?: () => void;
}) => {
  const container = useScreenModalContainer();
  const [render, setRender] = useState(open);
  // 退場アニメ中は親の state（target=null 等）で children が別物に再計算されるため、
  // open の間の children を凍結し、閉じ際はそれを表示して内容の flip を防ぐ。
  const frozen = useRef<ReactNode>(children);
  if (open) frozen.current = children;
  const shown = open ? children : frozen.current;

  useEffect(() => {
    if (open) setRender(true);
  }, [open]);

  if (!render || !container) return null;

  const animationEnd = () => {
    if (!open) setRender(false);
  };

  return createPortal(
    <div
      onAnimationEnd={animationEnd}
      onClick={onBackdropClose}
      className={`flex-col-el flex-center absolute inset-0 pointer-events-auto modal-overlay bg-textcolor-black/50 ${
        open ? "fade-in" : "fade-out"
      }`}
    >
      {/* backdrop は fade、パネルは弾む pop で登場。カード内クリックは閉じない */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={`flex-col-el flex-center modal-pop ${open ? "pop-in" : "pop-out"}`}
      >
        {shown}
      </div>
    </div>,
    container,
  );
};
